import type { IsolationMatrixSpec } from '../model.js';
import { isolationCellId } from '../families/isolation-matrix.js';

export function isolationMatrixFixture(
  width = 640,
  stress = false,
  focusCellId?: string,
): IsolationMatrixSpec {
  return {
    id: stress ? 'isolation-matrix-stress' : 'isolation-matrix',
    family: 'isolation-matrix',
    view: 'table',
    viewport: { width, height: 470 },
    columns: stress
      ? [
          { id: 'dirty-read', label: 'Dirty reads of uncommitted transactional values', shortLabel: 'DIRTY READS FROM UNCOMMITTED WRITES' },
          { id: 'non-repeatable-read', label: 'Non-repeatable reads after a concurrent commit', shortLabel: 'NON-REPEATABLE READ' },
          { id: 'phantom-read', label: 'Phantom rows appearing between repeated predicates', shortLabel: 'PHANTOM RESULT SET' },
          { id: 'write-skew', label: 'Write skew across independently valid row updates', shortLabel: 'WRITE SKEW ANOMALY' },
        ]
      : [
          { id: 'dirty-read', label: 'Dirty read', shortLabel: 'DIRTY' },
          { id: 'non-repeatable-read', label: 'Non-repeatable read', shortLabel: 'NON-REPEAT' },
          { id: 'phantom-read', label: 'Phantom read', shortLabel: 'PHANTOM' },
          { id: 'write-skew', label: 'Write skew', shortLabel: 'WRITE SKEW' },
        ],
    rows: stress
      ? [
          {
            id: 'read-uncommitted',
            label: 'Read uncommitted (weakest standard guarantee)',
            description: 'Permits observation of work that another transaction has not committed.',
            values: { 'dirty-read': 'allowed', 'non-repeatable-read': 'allowed', 'phantom-read': 'allowed', 'write-skew': 'allowed' },
          },
          {
            id: 'read-committed',
            label: 'Read committed (statement-level snapshot)',
            description: 'Each statement sees committed data, but consecutive statements can see different worlds.',
            values: { 'dirty-read': 'prevented', 'non-repeatable-read': 'allowed', 'phantom-read': 'allowed', 'write-skew': 'allowed' },
          },
          {
            id: 'repeatable-read',
            label: 'Repeatable read (implementation-dependent snapshot semantics)',
            description: 'Repeated reads remain stable; phantom behavior varies by implementation.',
            values: { 'dirty-read': 'prevented', 'non-repeatable-read': 'prevented', 'phantom-read': 'depends', 'write-skew': 'allowed' },
          },
          {
            id: 'serializable',
            label: 'Serializable (equivalent to one-at-a-time execution)',
            description: 'The result must match some valid serial ordering of the transactions.',
            values: { 'dirty-read': 'prevented', 'non-repeatable-read': 'prevented', 'phantom-read': 'prevented', 'write-skew': 'prevented' },
          },
        ]
      : [
          {
            id: 'read-uncommitted',
            label: 'Read uncommitted',
            description: 'Can observe another transaction before it commits.',
            values: { 'dirty-read': 'allowed', 'non-repeatable-read': 'allowed', 'phantom-read': 'allowed', 'write-skew': 'allowed' },
          },
          {
            id: 'read-committed',
            label: 'Read committed',
            description: 'Every statement observes committed data.',
            values: { 'dirty-read': 'prevented', 'non-repeatable-read': 'allowed', 'phantom-read': 'allowed', 'write-skew': 'allowed' },
          },
          {
            id: 'repeatable-read',
            label: 'Repeatable read',
            description: 'Repeated reads stay stable; phantom behavior varies by database engine.',
            values: { 'dirty-read': 'prevented', 'non-repeatable-read': 'prevented', 'phantom-read': 'depends', 'write-skew': 'allowed' },
          },
          {
            id: 'serializable',
            label: 'Serializable',
            description: 'Concurrent execution is equivalent to a valid serial order.',
            values: { 'dirty-read': 'prevented', 'non-repeatable-read': 'prevented', 'phantom-read': 'prevented', 'write-skew': 'prevented' },
          },
        ],
    ...(focusCellId ? { focusCellId } : {}),
    provenance: 'SQL isolation-level teaching matrix · standard phenomena with implementation-dependent behavior called out explicitly',
  };
}

export const defaultIsolationFocus = isolationCellId('repeatable-read', 'phantom-read');
